import React, { useEffect, useState } from 'react';

import { UploadOutlined } from '@ant-design/icons';
import '@ckeditor/ckeditor5-build-classic/build/translations/es';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  GetProp,
  Image,
  Modal,
  Row,
  Select,
  Upload,
  UploadFile,
  UploadProps,
  message,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { UploadChangeParam } from 'antd/es/upload';
import { RcFile } from 'antd/es/upload/interface';
import { AxiosProgressEvent } from 'axios';
import 'ckeditor5/ckeditor5.css';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import CustomCkEditor from '../../../../components/CustomCkEditor/CustomCkEditor';
import appI18n from '../../../../config/i18n';
import { ApiErrorData } from '../../../../models/ApiResponse';
import { Country } from '../../../../models/Country';
import { ItemInterface } from '../../../../models/ItemInterface';
import { DashboardNewService } from '../../../../services/DashboardNewService';
import { ImageHelper } from '../../../../utils/ImageHelper';

dayjs.extend(isSameOrAfter);

interface NewDashboardNewModalProps {
  isOpen: boolean;
  handleModalCancel: () => void;
  submit: (
    countryCode: string,
    title: string,
    content: string,
    logoUrl: string,
    publishedDate: string,
    expirationDate: string | null
  ) => void;
  countries: Country[];
}

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const getBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

function NewDashboardNewModal({
  isOpen,
  handleModalCancel,
  submit,
  countries,
}: NewDashboardNewModalProps) {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const dateFormat: string = useSelector((state: any) => state.intl.dateFormat);

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  const [editorData, setEditorData] = useState('');
  const [showEditor, setShowEditor] = useState(0);

  useEffect(() => {
    setLoading(false);
    form.resetFields();
    setEditorData('');
    setFileList([]);

    if (isOpen && showEditor === 0) {
      setTimeout(() => {
        setShowEditor(showEditor + 1);
      }, 200);
    }
  }, [isOpen]);

  const onFinish = async (values: any) => {
    if (editorData.trim().length === 0) {
      messageApi.error(
        t(
          'scenes.settings.news.components.new_dashboard_new_modal.validations.content.required'
        )
      );
      return;
    }

    if (fileList.length === 0) {
      messageApi.error(
        t(
          'scenes.settings.news.components.new_dashboard_new_modal.validations.logo.required'
        )
      );
      return;
    }

    const file = fileList[0];
    const logoUrl = file.response?.url;
    const statusFile = file.status;

    switch (statusFile) {
      case 'uploading':
        messageApi.info(
          t(
            'scenes.settings.news.components.new_dashboard_new_modal.upload.info_uploading'
          )
        );
        return;
      case 'error':
        messageApi.error(
          t(
            'scenes.settings.news.components.new_dashboard_new_modal.upload.error_upload_failed'
          )
        );
        return;
    }

    if (!logoUrl) {
      messageApi.error(
        t(
          'scenes.settings.news.components.new_dashboard_new_modal.upload.error_upload_failed'
        )
      );
      return;
    }

    setLoading(true);

    const publishedDate = values.publishedDate.format('YYYY-MM-DD');
    const expiredDate = values.expiredDate
      ? values.expiredDate.format('YYYY-MM-DD')
      : null;

    await submit(
      values.countryCode,
      values.title,
      editorData,
      logoUrl,
      publishedDate,
      expiredDate
    );

    setLoading(false);
  };

  const getCountries = (): ItemInterface[] => {
    return countries.map((record) => {
      return {
        value: record.code,
        label: record.name,
      };
    });
  };

  const beforeUpload = (file: FileType) => {
    const isValidType =
      file.type === 'image/jpeg' ||
      file.type === 'image/png' ||
      file.type === 'image/webp';
    if (!isValidType) {
      messageApi.error(
        t(
          'scenes.settings.news.components.new_dashboard_new_modal.upload.error_invalid_type'
        )
      );
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleChange = ({
    fileList: newFileList,
  }: UploadChangeParam<UploadFile>) => {
    setFileList(newFileList);
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as FileType);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const uploadProps: UploadProps = {
    beforeUpload: beforeUpload,
    fileList,
    listType: 'picture-card',
    showUploadList: true,
    onChange: handleChange,
    maxCount: 1,
    onPreview: handlePreview,
  };

  return (
    <>
      {contextHolder}

      <Modal
        title="Crear noticia"
        open={isOpen}
        onCancel={handleModalCancel}
        maskClosable={false}
        destroyOnClose
        footer={null}
        width={720}
      >
        <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="title"
                label="Título de la noticia"
                rules={[
                  { required: true, message: t('common.validations.required') },
                ]}
              >
                <TextArea
                  placeholder={t(
                    'scenes.settings.news.components.new_dashboard_new_modal.placeholders.title'
                  )}
                  rows={2}
                  disabled={loading}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="countryCode"
                label={t(
                  'scenes.settings.news.components.new_dashboard_new_modal.labels.country'
                )}
                rules={[
                  { required: true, message: t('common.validations.required') },
                ]}
              >
                <Select
                  style={{ width: '100%' }}
                  options={getCountries()}
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.news.components.new_dashboard_new_modal.placeholders.country'
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} md={12} lg={12} xl={12}>
              <Form.Item
                name="publishedDate"
                label={t(
                  'scenes.settings.news.components.new_dashboard_new_modal.labels.published_date'
                )}
                rules={[
                  { required: true, message: t('common.validations.required') },
                ]}
              >
                <DatePicker
                  allowClear={false}
                  style={{ width: '100%' }}
                  format={dateFormat}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={12} xl={12}>
              <Form.Item
                name="expiredDate"
                label={t(
                  'scenes.settings.news.components.new_dashboard_new_modal.labels.expired_date'
                )}
                dependencies={['publishedDate']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const publishedDate = getFieldValue('publishedDate');
                      if (
                        !value ||
                        !publishedDate ||
                        dayjs(value).isSameOrAfter(dayjs(publishedDate), 'day')
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          t(
                            'scenes.settings.news.components.new_dashboard_new_modal.validations.expired_date.invalid'
                          )
                        )
                      );
                    },
                  }),
                ]}
              >
                <DatePicker
                  allowClear
                  style={{ width: '100%' }}
                  format={dateFormat}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item
                label={t(
                  'scenes.settings.news.components.new_dashboard_new_modal.labels.content'
                )}
                required={true}
              >
                {showEditor > 0 && (
                  <CustomCkEditor
                    key={isOpen ? 'editor-open' : 'editor-closed'}
                    data={editorData}
                    setData={setEditorData}
                    placeholder={t(
                      'scenes.settings.news.components.new_dashboard_new_modal.placeholders.content'
                    )}
                    uploadUrl="/dashboard-new/upload-content-image"
                    disabled={loading}
                  />
                )}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item
                label={t(
                  'scenes.settings.news.components.new_dashboard_new_modal.labels.logo'
                )}
                required={true}
              >
                <Upload
                  {...uploadProps}
                  disabled={loading}
                  customRequest={async ({
                    file,
                    onSuccess,
                    onError,
                    onProgress,
                  }: any) => {
                    try {
                      const optimized = await ImageHelper.optimizeImageToWebP(
                        file as RcFile
                      );

                      if (
                        !ImageHelper.isImageSizeValid(optimized.optimizedSize)
                      ) {
                        onError?.(
                          new Error(
                            t(
                              'scenes.settings.news.components.new_dashboard_new_modal.upload.error_size_limit'
                            )
                          )
                        );
                        return;
                      }

                      const formData = new FormData();
                      formData.append('file', optimized.file);

                      const response = await DashboardNewService.uploadLogoFile(
                        formData,
                        (progressEvent: AxiosProgressEvent) => {
                          if (onProgress && progressEvent.total) {
                            const percent = Math.round(
                              (progressEvent.loaded * 100) / progressEvent.total
                            );
                            onProgress({ percent });
                          }
                        }
                      );

                      if (response.success) {
                        onSuccess?.(response.data);
                      } else {
                        const error = response.data as ApiErrorData;
                        throw new Error(
                          error.message ||
                            t(
                              'scenes.settings.news.components.new_dashboard_new_modal.upload.error_logo_server'
                            )
                        );
                      }
                    } catch (error: any) {
                      onError?.(error);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <button
                    style={{ border: 0, background: 'none', cursor: 'pointer' }}
                    type="button"
                    disabled={loading}
                  >
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>
                      {t(
                        'scenes.settings.news.components.new_dashboard_new_modal.buttons.upload_image'
                      )}
                    </div>
                  </button>
                </Upload>
              </Form.Item>
              {previewImage && (
                <Image
                  wrapperStyle={{ display: 'none' }}
                  preview={{
                    visible: previewOpen,
                    onVisibleChange: (visible) => setPreviewOpen(visible),
                    afterOpenChange: (visible) =>
                      !visible && setPreviewImage(''),
                  }}
                  src={previewImage}
                />
              )}
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Button
                onClick={() => {
                  form.submit();
                }}
                loading={loading}
                type="primary"
                block
              >
                {t(
                  'scenes.settings.news.components.new_dashboard_new_modal.buttons.save_changes'
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default NewDashboardNewModal;
